import { LinearGradient } from 'expo-linear-gradient';
import { useEffect, useRef } from 'react';
import { Animated, StyleSheet, View } from 'react-native';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';
import { Text } from './Text';

interface ProgressBarProps {
  value: number;
  color?: string;
  gradient?: boolean;
  animated?: boolean;
  height?: number;
  showLabel?: boolean;
}

const DEFAULT_HEIGHT = 6;

function clampValue(value: number): number {
  return Math.min(100, Math.max(0, value));
}

export function ProgressBar({
  value,
  color = colors.primary[500],
  gradient = false,
  animated = false,
  height = DEFAULT_HEIGHT,
  showLabel = false,
}: Readonly<ProgressBarProps>) {
  const clamped = clampValue(value);
  const animatedWidth = useRef(new Animated.Value(clamped)).current;

  useEffect(() => {
    if (animated) {
      Animated.timing(animatedWidth, {
        toValue: clamped,
        duration: tokens.duration.slow,
        useNativeDriver: false,
      }).start();
    } else {
      animatedWidth.setValue(clamped);
    }
  }, [clamped, animated, animatedWidth]);

  const widthInterpolation = animatedWidth.interpolate({
    inputRange: [0, 100],
    outputRange: ['0%', '100%'],
    extrapolate: 'clamp',
  });

  const trackStyle = [styles.track, { height, borderRadius: height / 2 }];

  const fillStyle = [
    styles.fill,
    {
      height,
      borderRadius: height / 2,
      width: widthInterpolation,
    },
    !gradient && { backgroundColor: color },
  ];

  return (
    <View
      style={styles.container}
      accessibilityRole="progressbar"
      accessibilityValue={{ min: 0, max: 100, now: Math.round(clamped) }}
    >
      <View style={trackStyle}>
        <Animated.View style={fillStyle}>
          {gradient ? (
            <LinearGradient
              colors={colors.primary.gradient as unknown as [string, string]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={[StyleSheet.absoluteFill, { borderRadius: height / 2 }]}
            />
          ) : null}
        </Animated.View>
      </View>
      {showLabel ? (
        <Text variant="caption" color={colors.text.tertiary} style={styles.label}>
          {Math.round(clamped)}%
        </Text>
      ) : null}
    </View>
  );
}

ProgressBar.displayName = 'ProgressBar';

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  track: {
    width: '100%',
    backgroundColor: colors.background.input,
    overflow: 'hidden',
  },
  fill: {
    overflow: 'hidden',
  },
  label: {
    marginTop: tokens.spacing.xs,
  },
});
